import { queryOptions } from "@tanstack/react-query";
import type { ScopedBinding } from "@/app/types";
import type { ApiClient } from "@/lib/api";
import { getEngineApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { Plugin, PluginListResponse } from "../types";
import { pluginsKeys } from "./query-keys";

export const fetchPlugins = async ({
  client,
}: {
  client: ApiClient;
}): Promise<Plugin[]> => {
  const { data } = await client.get<PluginListResponse | Plugin[]>("/plugins");
  if (Array.isArray(data)) {
    return data;
  }
  return data?.items ?? [];
};

export const pluginsQueryOptions = ({
  binding = "project",
  scope,
}: {
  binding?: ScopedBinding;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<Plugin[]>({
    queryKey: pluginsKeys.list({ binding, scope }),
    queryFn: () => fetchPlugins({ client }),
  });
};

export const fetchPlugin = async ({
  client,
  id,
  binding = "project",
}: {
  client: ApiClient;
  id: string;
  binding?: ScopedBinding;
}): Promise<Plugin> => {
  const { data } = await client.get<Plugin>(`/plugins/${id}`, {
    params: { binding_type: binding },
  });
  return data;
};

export const pluginQueryOptions = ({
  id,
  binding = "project",
  scope,
}: {
  id: string;
  binding?: ScopedBinding;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<Plugin>({
    queryKey: pluginsKeys.detail({ binding, scope, id }),
    queryFn: () => fetchPlugin({ client, id, binding }),
  });
};
